import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Search, UserRound, UserX } from "lucide-react";

function AssignmentSection({
  canAssign = false,
  users = [],
  assignedUser = null,
  loading = false,
  error = "",
  onAssignUser,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [assignmentError, setAssignmentError] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const normalizedUsers = Array.isArray(users)
    ? users.filter((member) => !member.status || member.status === "Active")
    : [];
  const filteredUsers = normalizedUsers.filter((user) => {
    const name =
      user?.user?.name ||
      user?.user?.username ||
      user?.name ||
      user?.full_name ||
      user?.username ||
      "";
    const email = user?.user?.email || user?.email || "";
    const value = `${name} ${email}`.toLowerCase();

    return value.includes(search.toLowerCase());
  });

  const selectedUser =
    normalizedUsers.find(
      (member) => (member.user?.id || member.id) === assignedUser?.id,
    )?.user ||
    assignedUser ||
    null;
  const selectedName =
    selectedUser?.user?.name ||
    selectedUser?.user?.username ||
    selectedUser?.name ||
    selectedUser?.full_name ||
    selectedUser?.username ||
    "Unassigned";

  const getInitials = (name) => {
    const value = String(name || "").trim();

    if (!value) return "?";

    const parts = value.split(/\s+/);

    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const handleSelect = async (user) => {
    if (!canAssign || loading || saving) return;
    setSaving(true);
    setAssignmentError("");

    try {
      await onAssignUser?.(user);
      setIsOpen(false);
      setSearch("");
    } catch (error) {
      setAssignmentError(
        error?.response?.data?.message ||
          "Could not update the assignment. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={dropdownRef}
      className="relative min-w-0"
      onKeyDown={(event) => {
        if (event.key === "Escape") setIsOpen(false);
      }}
    >
      {(error || assignmentError) && (
        <p role="alert" className="mb-2 text-xs text-danger">
          {error || assignmentError}
        </p>
      )}
      {!canAssign && (
        <p className="mb-2 text-xs text-text-muted">
          Your manager or administrator manages assignments.
        </p>
      )}
      <button
        type="button"
        disabled={!canAssign || loading || saving}
        aria-expanded={canAssign && isOpen}
        aria-label="Assign conversation"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex min-h-[58px] w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-all duration-200 ${
          isOpen
            ? "border-theme-200 bg-theme-50/50 shadow-[0_0_0_3px_rgba(61,155,210,0.08)] dark:border-dark-primary/40 dark:bg-dark-soft-blue"
            : "border-border-light bg-card-bg hover:border-theme-100 hover:bg-theme-50/30 dark:border-dark-border dark:bg-dark-card dark:hover:border-dark-primary/30 dark:hover:bg-dark-soft-blue"
        } ${loading ? "cursor-not-allowed opacity-60" : ""}`}
      >
        {/* Avatar */}
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
            selectedUser
              ? "border border-theme-100 bg-theme-50 text-primary dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-primary"
              : "border border-border-light bg-app-bg text-text-muted dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-text-muted"
          }`}
        >
          {selectedUser ? (
            <span className="text-[11px] font-bold">
              {getInitials(selectedName)}
            </span>
          ) : (
            <UserX size={17} strokeWidth={1.8} />
          )}
        </div>

        {/* User Info */}
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-text-muted dark:text-dark-text-muted">
            Assigned to
          </p>

          <p
            className={`mt-0.5 truncate text-[13px] font-semibold ${
              selectedUser
                ? "text-text-primary dark:text-dark-text-primary"
                : "text-text-muted dark:text-dark-text-muted"
            }`}
          >
            {selectedName}
          </p>
        </div>

        {/* Assigned */}
        {selectedUser && (
          <div className="hidden shrink-0 items-center gap-1.5 sm:flex">
            <span className="h-1.5 w-1.5 rounded-full bg-[#6fa783]" />

            <span className="text-[10px] font-medium text-text-muted dark:text-dark-text-muted">
              Assigned
            </span>
          </div>
        )}

        {/* Chevron */}
        <ChevronDown
          size={17}
          strokeWidth={1.8}
          className={`shrink-0 text-text-muted transition-transform duration-200 dark:text-dark-text-muted ${
            isOpen ? "rotate-180 text-primary dark:text-dark-primary" : ""
          }`}
        />
      </button>

      {/* Dropdown */}
      {canAssign && isOpen && (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-[70] overflow-hidden rounded-md border border-border-light bg-card-bg shadow-[0_16px_45px_rgba(15,23,42,0.12)] dark:border-dark-border dark:bg-dark-card dark:shadow-[0_16px_45px_rgba(0,0,0,0.28)]">
          <p className="px-3 pt-3 text-[11px] text-text-muted dark:text-dark-text-muted">
            Organisation members with WhatsApp access
          </p>
          {/* Search */}
          <div className="border-b border-border-light bg-card-bg p-2.5 dark:border-dark-border dark:bg-dark-card">
            <div className="flex h-9 items-center gap-2 rounded-lg border border-border-light bg-app-bg px-2.5 transition-all focus-within:border-theme-100 focus-within:bg-card-bg focus-within:shadow-[0_0_0_3px_rgba(61,155,210,0.06)] dark:border-dark-border dark:bg-dark-soft-blue dark:focus-within:border-dark-primary/30 dark:focus-within:bg-dark-card">
              <Search
                size={15}
                strokeWidth={1.8}
                className="shrink-0 text-text-muted dark:text-dark-text-muted"
              />

              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                aria-label="Search organisation members"
                placeholder="Search name or email..."
                className="min-w-0 flex-1 bg-transparent text-[11px] text-text-primary outline-none placeholder:text-text-muted dark:text-dark-text-primary dark:placeholder:text-dark-text-muted"
                autoFocus
              />
            </div>
          </div>

          {/* User List */}
          <div className="max-h-[min(360px,calc(100vh-250px))] overflow-y-auto overscroll-contain p-1.5">
            {/* Unassigned */}
            <button
              type="button"
              disabled={!canAssign || loading || saving}
              onClick={() => handleSelect(null)}
              className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left transition-colors duration-150 ${
                !selectedUser
                  ? "bg-theme-50 dark:bg-dark-soft-blue"
                  : "hover:bg-app-bg dark:hover:bg-dark-soft-blue"
              }`}
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border-light bg-app-bg text-text-muted dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-text-muted">
                <UserX size={15} strokeWidth={1.8} />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold text-text-primary dark:text-dark-text-primary">
                  Unassigned
                </p>

                <p className="mt-0.5 text-[10px] text-text-muted dark:text-dark-text-muted">
                  Remove assignment
                </p>
              </div>

              {!selectedUser && (
                <Check
                  size={15}
                  strokeWidth={2}
                  className="shrink-0 text-primary dark:text-dark-primary"
                />
              )}
            </button>

            {filteredUsers.length > 0 && (
              <div className="my-1.5 border-t border-border-light dark:border-dark-border" />
            )}

            {/* Users */}
            {filteredUsers.map((user) => {
              const userData = user?.user || user;
              const userName =
                userData?.name ||
                userData?.full_name ||
                userData?.username ||
                "Unknown User";
              const email = userData?.email || "";
              const userId = userData?.id;
              const isSelected = selectedUser?.id === userId;
              const isOnline =
                userData?.status === "online" ||
                userData?.status === "active" ||
                userData?.is_online === true;

              return (
                <button
                  key={userId}
                  type="button"
                  disabled={!canAssign || loading || saving}
                  onClick={() => handleSelect(userData)}
                  className={`group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left transition-colors duration-150 ${
                    isSelected
                      ? "bg-theme-50 dark:bg-dark-soft-blue"
                      : "hover:bg-app-bg dark:hover:bg-dark-soft-blue"
                  }`}
                >
                  {/* Avatar */}
                  <div
                    className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                      isSelected
                        ? "border border-theme-100 bg-theme-50 text-primary dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-primary"
                        : "border border-border-light bg-app-bg text-text-secondary dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-text-secondary"
                    }`}
                  >
                    <span className="text-[10px] font-bold">
                      {getInitials(userName)}
                    </span>

                    {isOnline && (
                      <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-card-bg bg-[#6fa783] dark:border-dark-card" />
                    )}
                  </div>

                  {/* User */}
                  <div className="min-w-0 flex-1">
                    <p
                      className={`truncate text-[12px] font-semibold ${
                        isSelected
                          ? "text-primary dark:text-dark-primary"
                          : "text-text-primary dark:text-dark-text-primary"
                      }`}
                    >
                      {userName}
                    </p>

                    {email && (
                      <p className="mt-0.5 truncate text-[10px] text-text-muted dark:text-dark-text-muted">
                        {email}
                      </p>
                    )}
                  </div>

                  {user?.role && (
                    <span className="text-[10px] text-text-muted dark:text-dark-text-muted">
                      {user.role === "Agent" ? "Member" : user.role}
                    </span>
                  )}
                  {/* Selected */}
                  {isSelected && (
                    <Check
                      size={15}
                      strokeWidth={2}
                      className="shrink-0 text-primary dark:text-dark-primary"
                    />
                  )}
                </button>
              );
            })}

            {/* Empty */}
            {!filteredUsers.length && (
              <div className="px-3 py-8 text-center">
                <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl border border-border-light bg-app-bg text-text-muted dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-text-muted">
                  <UserRound size={16} strokeWidth={1.8} />
                </div>

                <p className="mt-2.5 text-[11px] font-semibold text-text-secondary dark:text-dark-text-secondary">
                  No eligible members found
                </p>

                <p className="mt-1 text-[10px] text-text-muted dark:text-dark-text-muted">
                  Search by name or email. Manage app access in Auto Appy.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AssignmentSection;
